import { mergeRefs } from '@fe-learner-common/utils/merge-refs';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { cn, FaIcon, Spinner } from 'fe-learner-common';
import { FC, forwardRef, Ref, useMemo, useRef } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

type SearchInputProps = {
  className?: string;
  id: string;
  defaultValue?: string;
  onSearch: (value: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  inputClassName?: string;
  isLoading?: boolean;
  texts: {
    placeholder: string;
    clearSearchAriaLabel: string;
    loadingAriaLabel?: string;
  };
};

export const SearchInput: FC<SearchInputProps> = forwardRef<
  HTMLFormElement,
  SearchInputProps
>(
  (
    {
      className,
      id,
      defaultValue,
      texts,
      inputRef,
      inputClassName,
      isLoading,
      onSearch,
    },
    ref,
  ) => {
    const localInputRef = useRef<HTMLInputElement | null>(null);
    const inputRefs = useMemo(
      () => mergeRefs(localInputRef, inputRef),
      [inputRef],
    );

    const onClear = () => {
      onSearch('');
      if (localInputRef.current) {
        localInputRef.current.value = '';
        localInputRef.current.focus();
      }
    };

    const debouncedOnSearch = useDebounceCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onSearch(event.target.value);
      },
      200,
    );

    return (
      <form
        ref={ref}
        className={cn('lf:l-search-form lf:relative', className)}
        onSubmit={(event) => event.preventDefault()}
      >
        <div role="search">
          <label
            className="lf:pointer-events-none lf:absolute lf:inset-y-0 lf:start-0 lf:flex lf:items-center lf:ps-3"
            htmlFor={id}
            aria-labelledby={`${id}SearchTitle`}
          >
            <FaIcon icon={faMagnifyingGlass} width={16} height={16} />
            <title id={`${id}SearchTitle`}>{texts.placeholder}</title>
          </label>
          <input
            ref={inputRefs}
            defaultValue={defaultValue}
            id={id}
            type="search"
            onChange={debouncedOnSearch}
            placeholder={texts.placeholder}
            className={cn('lf:l-input lf:l-input-md lf:ps-10', inputClassName)}
          />
          <span className="lf:sr-only" aria-live="polite" aria-busy={isLoading}>
            {isLoading ? texts.loadingAriaLabel : ''}
          </span>
          {isLoading ? (
            <span className="lf:pointer-events-none lf:absolute lf:end-2.5 lf:top-1/2 lf:-translate-y-1/2 lf:text-neutral-550">
              <Spinner name={`${id}-spinner`} />
            </span>
          ) : (
            <button
              type="button"
              onClick={onClear}
              className="lf:l-btn lf:l-icon-btn lf:l-btn-secondary lf:l-btn-sm lf:absolute lf:end-0 lf:top-1/2 lf:hidden lf:-translate-y-1/2 lf:text-neutral-550"
              aria-label={texts.clearSearchAriaLabel}
            >
              <FaIcon icon={faCircleXmark} width={16} height={16} />
            </button>
          )}
        </div>
      </form>
    );
  },
);
SearchInput.displayName = 'SearchInput';
